import './customer-overviews.css';
import { useRef, useState } from 'react';
import { tapPageIndexingSchema } from '@inktap/contracts/tap-page-indexing';
import { Link } from 'react-router-dom';

import { TapPageIndexingPanel } from '../components/TapPageIndexingPanel';
import { TapPageSharePanel } from '../components/TapPageSharePanel';
import { ProtectedResourceAccessPanel } from '../components/ProtectedResourceAccessPanel';
import { TapActionIcon } from '../features/tap-builder/TapActionIcon';
import { getActionLabel, type TapAction } from '../lib/tapDocument';
import { useSession } from '../session';
import { formatDate } from './portalTypes';
import { useDashboardSummary } from './useDashboardSummary';

const iconType = (type: string): TapAction['type'] => {
  switch (type) {
    case 'download-card': case 'booking': case 'service-book': case 'website':
    case 'social': case 'file': case 'custom':
      return type;
    default:
      return 'custom';
  }
};

function TapPagePortalPage() {
  const { summary, loading, error, reload } = useDashboardSummary();
  const { session } = useSession();
  const previewRef = useRef<HTMLElement>(null);
  const [photoFailure, setPhotoFailure] = useState<object | null>(null);
  const [photoAttempt, setPhotoAttempt] = useState(0);
  const tapPage = summary?.tapPage ?? null;
  const preview = tapPage?.preview ?? null;
  const profile = preview?.profile;
  // A retired image event cannot affect a later account, summary read, or retry.
  const photoScope = JSON.stringify([session?.email, session?.slug, session?.expiresAt,
    session?.active, loading, profile?.photoUrl, photoAttempt]);
  const photoOwner = useRef({ scope: photoScope, session, summary });
  if (photoOwner.current.scope !== photoScope || photoOwner.current.session !== session || photoOwner.current.summary !== summary) {
    photoOwner.current = { scope: photoScope, session, summary };
  }
  const owner = photoOwner.current;
  const photoFailed = photoFailure === owner;
  const readyToShare = tapPage?.sharing?.state === 'ready';
  const isPublished = tapPage?.status === 'published';
  const parsedIndexing = tapPageIndexingSchema.safeParse(tapPage?.indexing);
  const indexingAvailable = parsedIndexing.success && parsedIndexing.data.eligible;

  return (
    <div className="dashboard-page portal-page portal-workspace tap-overview">
      <header className="portal-intro">
        <h1 tabIndex={-1}>Your tap page</h1>
        <p>
          {isPublished
            ? `Published ${formatDate(tapPage?.publishedAt)}${readyToShare ? ' and ready to share' : ''}.`
            : 'Finish your profile and publish it when you are ready to share.'}
        </p>
      </header>

      {error ? <div className="overview-recovery"><div className="alert" role="alert">{error}</div><button type="button" className="button-secondary" onClick={(event) => { event.currentTarget.closest('.portal-workspace')?.querySelector('h1')?.focus(); void reload(); }}>Retry account summary</button></div> : null}

      {loading ? (
        <section className="dashboard-section" aria-live="polite">
          <p className="helper-text">Loading your tap page...</p>
        </section>
      ) : summary ? (
        <>
          <div className="tap-overview__workspace">
            <section ref={previewRef} tabIndex={-1} className={`tap-phone-preview tap-phone-preview--${preview?.theme ?? 'auto'}`} aria-label="Tap page preview">
              <div className="tap-phone-preview__screen">
                {profile?.photoUrl && !photoFailed ? (
                  <img key={photoScope} className="tap-phone-preview__photo" src={profile.photoUrl} alt=""
                    onError={() => { if (photoOwner.current === owner) setPhotoFailure(owner); }} />
                ) : (
                  <span className="tap-phone-preview__initial" aria-hidden="true">
                    {(profile?.name ?? 'I').charAt(0).toUpperCase()}
                  </span>
                )}
                <h2>{profile?.name ?? 'Your name'}</h2>
                <p className="tap-phone-preview__title">{profile?.title ?? 'Your title'}</p>
                {profile?.subtitle ? <p className="tap-phone-preview__subtitle">{profile.subtitle}</p> : null}
                {profile?.bio ? <p className="tap-phone-preview__bio">{profile.bio}</p> : null}
                <div className="tap-phone-preview__actions" aria-label="Preview actions">
                  {(preview?.actions ?? []).slice(0, 4).map((action) => (
                    <div className="tap-phone-preview__action" key={action.id}>
                      <TapActionIcon type={iconType(action.type)} />
                      <span>{getActionLabel({ ...action, type: action.type as TapAction['type'], label: action.label ?? undefined, provider: (action.provider ?? undefined) as TapAction['provider'] })}</span>
                    </div>
                  ))}
                  {!preview?.actions.length ? <span>Add your first action</span> : null}
                  {preview && preview.actions.length > 4 ? <small>And {preview.actions.length - 4} more {preview.actions.length === 5 ? 'action' : 'actions'}</small> : null}
                </div>
              </div>
              {photoFailed ? <button type="button" className="tap-phone-preview__retry"
                onClick={() => { previewRef.current?.focus({ preventScroll: true }); setPhotoAttempt(attempt => attempt + 1); }}>
                Retry profile photo
              </button> : null}
            </section>

            <section className="tap-manage-panel">
              <div>
                <h2>Manage your page</h2>
                <p>Update what people see, open the live page, or copy its share link.</p>
              </div>
              <div className="tap-manage-panel__actions">
                <Link className="btn-primary" to="/builder">Edit page</Link>
              </div>

              {tapPage ? <TapPageSharePanel slugId={tapPage.slugId} sharing={tapPage.sharing ?? { state: 'unavailable', kind: null, href: null }} /> : (
                <div className="tap-share-note"><h3>Share your page</h3><p>Create and publish your page to enable sharing.</p></div>
              )}
            </section>
          </div>

          <aside className="tap-alias-strip">
            <div>
              <strong>{summary.plan.entitlements.canManagePublicAlias === true ? tapPage?.publicSlug ? 'Public alias configured' : 'Public alias available' : 'Make your link easier to remember'}</strong>
              <span>
                {summary.plan.entitlements.canManagePublicAlias === true
                  ? tapPage?.publicSlug ? 'Your public alias is configured.' : indexingAvailable ? 'Create your public alias in the indexing settings below.' : 'Public alias settings are unavailable. Refresh your page status.'
                  : 'Boost adds a short public alias and discovery controls.'}
              </span>
            </div>
            {summary.plan.entitlements.canManagePublicAlias === true
              ? indexingAvailable ? <a href="#tap-indexing-panel">{tapPage?.publicSlug ? 'Indexing settings' : 'Create public alias'}</a> : null
              : <Link to="/plan-billing">Explore Boost</Link>}
          </aside>

          {tapPage ? <TapPageIndexingPanel slugId={tapPage.slugId} slug={tapPage.slug} indexing={tapPage.indexing} canManagePublicAlias={summary.plan.entitlements.canManagePublicAlias === true} onStatusConfirmed={signal => reload(false, signal)} /> : null}

          <ProtectedResourceAccessPanel />
        </>
      ) : null}
    </div>
  );
}

export default TapPagePortalPage;
